import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { SupplierDiversities, SupplierDiversityFilters } from '../models';
import * as supplierDiversitiesService from '../services/supplier-diversities.service';

export const useSupplierDiversities = (
  defaultFilters: Partial<SupplierDiversityFilters & { searchParams: { ids: string[] } }> = {},
) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<SupplierDiversityFilters>>(otherDefaultFilters);

  const cacheData = supplierDiversitiesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<SupplierDiversityFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(supplierDiversitiesService.supplierDiversitiesQueryOptions(filters));

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    supplierDiversitiesService.supplierDiversitiesQueryOptions(
      { search: { codeId: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );

  const mergedMap: SupplierDiversities['map'] = {
    ...(data?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
